import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AMBER, CAT_FILTERS, AGE_RANGES, SETTING_FILTERS, PRICE_FILTERS, RADIUS_OPTIONS, togArr,
} from '../lib/data';

function Chip({ label, active, color, onClick }) {
  const c = color || AMBER;
  return (
    <button
      onClick={onClick}
      style={{
        flexShrink: 0, padding: '6px 12px', borderRadius: 99,
        fontSize: 12, fontWeight: 600, border: `1px solid ${active ? c : '#e5e5e5'}`,
        background: active ? c : '#fff', color: active ? '#fff' : '#555',
        transition: 'all .15s', whiteSpace: 'nowrap',
        display: 'flex', alignItems: 'center', gap: 4,
        cursor: 'pointer', fontFamily: 'inherit',
      }}
    >
      {active && <span style={{ fontSize: 9 }}>✓</span>}
      {label}
    </button>
  );
}

export default function FilterBar({
  ages, cats, settings, prices, radius,
  setAges, setCats, setSettings, setPrices, setRadius,
  userLat,
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const total = ages.length + cats.length + settings.length + prices.length + (radius ? 1 : 0);

  function clearAll() {
    setAges([]); setCats([]); setSettings([]); setPrices([]); setRadius(null);
  }

  // Close on click outside
  useEffect(() => {
    function handleClickOutside(e) {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
      }
    }
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [open]);

  const sections = [
    {
      label: t('filters.age'),
      items: AGE_RANGES, cur: ages, fn: setAges,
      hint: ages.length > 1
        ? `👨‍👩‍👧‍👦 ${ages.map(id => AGE_RANGES.find(r => r.id === id).label).join(' i ')}`
        : null,
    },
    { label: t('filters.category'), items: CAT_FILTERS,     cur: cats,     fn: setCats,     colors: true },
    { label: t('filters.setting'),  items: SETTING_FILTERS, cur: settings, fn: setSettings },
    { label: t('filters.price'),    items: PRICE_FILTERS,   cur: prices,   fn: setPrices   },
  ];

  return (
    <div ref={ref} style={{ background: '#fff', borderBottom: '1px solid #f0f0f0', flexShrink: 0, zIndex: 20 }}>
      <div style={{ maxWidth: 480, margin: '0 auto' }}>

        {/* Scroll row */}
        <div style={{ display: 'flex', gap: 7, padding: '7px 14px', overflowX: 'auto', alignItems: 'center' }}
          className="scrollbar-hide">
          <button
            onClick={() => setOpen(x => !x)}
            style={{
              flexShrink: 0, display: 'flex', alignItems: 'center', gap: 5,
              padding: '6px 12px', borderRadius: 99, fontSize: 12, fontWeight: 700,
              border: `1px solid ${open ? AMBER : '#e5e5e5'}`,
              background: open ? AMBER : '#fff',
              color: open ? '#fff' : '#555',
              transition: 'all .18s', cursor: 'pointer', fontFamily: 'inherit',
            }}
          >
            ⚙️ {t('filters.title')}
            {total > 0 && (
              <span style={{
                background: open ? '#fff' : AMBER, color: open ? AMBER : '#fff',
                fontSize: 10, borderRadius: 99, minWidth: 15, height: 15,
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                fontWeight: 800, padding: '0 3px',
              }}>{total}</span>
            )}
          </button>

          {CAT_FILTERS.map(f => (
            <Chip key={f.id} label={f.label} active={cats.includes(f.id)} color={f.color}
              onClick={() => setCats(togArr(cats, f.id))} />
          ))}
        </div>

        {/* Expanded panel */}
        {open && (
          <div style={{ padding: '10px 14px 14px', borderTop: '1px solid #f5f5f5', display: 'flex', flexDirection: 'column', gap: 12 }}>

            {sections.map(({ label, items, cur, fn, hint, colors }) => (
              <div key={label}>
                <div style={{ fontSize: 10, fontWeight: 700, color: '#bbb', textTransform: 'uppercase', letterSpacing: .6, marginBottom: 5 }}>
                  {label}
                </div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {items.map(f => (
                    <Chip key={f.id} label={f.label} active={cur.includes(f.id)}
                      color={colors ? f.color : undefined}
                      onClick={() => fn(togArr(cur, f.id))} />
                  ))}
                </div>
                {hint && (
                  <div style={{ marginTop: 5, fontSize: 11, color: '#92400E', background: '#FEF3C7', borderRadius: 8, padding: '5px 8px' }}>
                    {hint}
                  </div>
                )}
              </div>
            ))}

            {/* Radius */}
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, color: '#bbb', textTransform: 'uppercase', letterSpacing: .6, marginBottom: 5 }}>
                Odległość
              </div>
              {userLat === null ? (
                <div style={{ fontSize: 12, color: '#bbb', background: '#f9f9f9', borderRadius: 10, padding: '8px 12px' }}>
                  📍 Włącz lokalizację, aby filtrować po odległości
                </div>
              ) : (
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {RADIUS_OPTIONS.map(f => (
                    <Chip key={f.id} label={f.label} active={radius === f.km}
                      onClick={() => setRadius(radius === f.km ? null : f.km)} />
                  ))}
                </div>
              )}
            </div>

            {total > 0 && (
              <button onClick={clearAll} style={{ fontSize: 11, fontWeight: 700, color: AMBER, background: 'none', border: 'none', textDecoration: 'underline', textAlign: 'left', padding: 0, cursor: 'pointer' }}>
                {t('filters.clearAll')}
              </button>
            )}

          </div>
        )}
      </div>
    </div>
  );
}